import { useState, useEffect } from 'react';
import { formatUnixTimestampTime } from '../utils/dateUtils';

const DocumentGenerationJobsTable = ({ clientId, clientHelperService, onControlsRender, documentTemplates = [] }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [jobToEdit, setJobToEdit] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [editingNameJobId, setEditingNameJobId] = useState(null);

  // Fetch jobs when clientId changes
  useEffect(() => {
    if (!clientId) {
      setJobs([]);
      return;
    }
    fetchJobs();
  }, [clientId]);

  // Pass controls to parent via callback
  useEffect(() => {
    if (onControlsRender) {
      const controls = (
        <div className="d-flex align-items-center">
          <div className="form-check form-switch mb-0 me-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="autoRefreshToggle"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              disabled={!clientId}
            />
            <label className="form-check-label small" htmlFor="autoRefreshToggle">
              Auto-refresh (1 min)
            </label>
          </div>
          <button 
            className="btn btn-sm btn-outline-secondary" 
            onClick={fetchJobs}
            disabled={loading || !clientId}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''} me-1`}></i>
            Refresh
          </button>
        </div>
      );
      onControlsRender(controls);
    }
  }, [autoRefresh, loading, clientId, onControlsRender]);

  // Set up auto-refresh interval when enabled
  useEffect(() => {
    if (!autoRefresh || !clientId) return;
    
    // Refresh every 1 minute when enabled
    const intervalId = setInterval(() => {
      fetchJobs();
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, [autoRefresh, clientId]);

  const fetchJobs = async () => {
    if (!clientId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await clientHelperService.getDocumentGenerationJobs(clientId);
      if (response.success) {
        setJobs(response.jobs || []);
      } else {
        setError('Failed to fetch jobs');
      }
    } catch (err) {
      console.error('Error fetching document generation jobs:', err);
      
      // Check if it's a 500 error - likely means the DynamoDB table doesn't exist yet
      if (err.response && err.response.status === 500) {
        setError('Document generation tracking is not yet configured. The DynamoDB table may not exist.');
      } else {
        setError(err.message || 'Failed to fetch jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success';
      case 'failed':
        return 'bg-danger';
      case 'processing':
        return 'bg-warning text-dark';
      case 'pending':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'bi-check-circle';
      case 'failed':
        return 'bi-x-circle';
      case 'processing':
        return 'bi-hourglass-split';
      case 'pending':
        return 'bi-clock';
      default:
        return 'bi-question-circle';
    }
  };

  const handleDownload = async (job, format = 'pdf') => {
    let outputs = [];
    if (job.output) {
      try {
        outputs = JSON.parse(job.output);
      } catch (err) {
        console.error('Error parsing job.output:', err);
        alert('Error parsing document outputs');
        return;
      }
    }
    
    if (!outputs || outputs.length === 0) {
      alert('Document not available for download');
      return;
    }

    // Find the file with the requested format
    const outputFile = outputs.find(path => path.toLowerCase().endsWith(`.${format}`));
    
    if (!outputFile) {
      alert(`${format.toUpperCase()} version not available`);
      return;
    }

    try {
      const { blob } = await clientHelperService.downloadGeneratedDocument(outputFile);
      
      // Extract filename from S3 path
      let filename = outputFile;
      if (filename.includes('/')) {
        filename = filename.split('/').pop();
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert(`Failed to download document: ${err.message}`);
    }
  };

  const handlePreview = async (job) => {
    let outputs = [];
    if (job.output) {
      try {
        outputs = JSON.parse(job.output);
      } catch (err) {
        console.error('Error parsing job.output:', err);
        alert('Error parsing document outputs');
        return;
      }
    }
    
    if (!outputs || outputs.length === 0) {
      alert('Document not available for preview');
      return;
    }

    // Always preview the PDF version
    const pdfFile = outputs.find(path => path.toLowerCase().endsWith('.pdf'));
    
    if (!pdfFile) {
      alert('PDF version not available for preview');
      return;
    }

    try {
      setPreviewLoading(true);
      
      // Extract filename from S3 path
      let filename = pdfFile;
      if (filename.includes('/')) {
        filename = filename.split('/').pop();
      }
      
      const { blob } = await clientHelperService.downloadGeneratedDocument(pdfFile);
      
      // Create blob URL for preview
      const previewBlob = new Blob([blob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(previewBlob);
      
      setPreviewUrl(url);
      setPreviewFilename(filename);
    } catch (err) {
      console.error('Error previewing document:', err);
      alert(`Failed to preview document: ${err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFilename('');
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch (err) {
      return isoString;
    }
  };

  const getTemplateName = (templateId) => {
    // First try to find the template in the provided templates data
    const template = documentTemplates.find(t => t.id === templateId);
    if (template) {
      return template.name;
    }
    
    // Fallback to hardcoded mapping for backward compatibility
    const templateNames = {
      'gs_template': 'Getting Started Document',
      'client_summary': 'Client Summary'
    };
    return templateNames[templateId] || templateId;
  };

  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;
    
    setDeletingJobId(jobToDelete.execution_id);
    setShowDeleteModal(false);
    
    try {
      await clientHelperService.deleteDocumentGenerationJob(jobToDelete.execution_id);
      
      // Remove the job from the local state
      setJobs(prevJobs => prevJobs.filter(job => job.execution_id !== jobToDelete.execution_id));
      
      // Optionally show success message
      // You could add a toast notification here if available
      console.log(`Successfully deleted job ${jobToDelete.execution_id}`);
      
    } catch (error) {
      console.error('Error deleting job:', error);
      alert(`Failed to delete job: ${error.message}`);
    } finally {
      setDeletingJobId(null);
      setJobToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setJobToDelete(null);
  };

  const handleEditNameClick = (job) => {
    setJobToEdit(job);
    setEditNameValue(job.document_name || '');
    setShowEditNameModal(true);
  };

  const handleEditNameConfirm = async () => {
    if (!jobToEdit) return;
    
    setEditingNameJobId(jobToEdit.execution_id);
    setShowEditNameModal(false);
    
    try {
      await clientHelperService.updateDocumentGenerationJob(jobToEdit.execution_id, {
        document_name: editNameValue.trim()
      });
      
      // Update the job in local state
      setJobs(prevJobs => prevJobs.map(job => 
        job.execution_id === jobToEdit.execution_id 
          ? { ...job, document_name: editNameValue.trim() }
          : job
      ));
      
      console.log(`Successfully updated job ${jobToEdit.execution_id} name to "${editNameValue.trim()}"`);
      
    } catch (error) {
      console.error('Error updating job name:', error);
      alert(`Failed to update document name: ${error.message}`);
    } finally {
      setEditingNameJobId(null);
      setJobToEdit(null);
      setEditNameValue('');
    }
  };

  const handleEditNameCancel = () => {
    setShowEditNameModal(false);
    setJobToEdit(null);
    setEditNameValue('');
  };

  return (
    <div>
      {/* Content area */}
      {!clientId ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-file-earmark-text display-4 mb-3"></i>
          <p>Select a client to view document generation jobs</p>
        </div>
      ) : loading && jobs.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading document generation jobs...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button 
            className="btn btn-sm btn-outline-danger ms-3" 
            onClick={fetchJobs}
          >
            Retry
          </button>
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-inbox display-4 mb-3"></i>
          <p>No document generation jobs found for this client</p>
          <p className="small">Start by generating a document using the form on the left</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Document</th>
              <th>Status</th>
              <th>Started</th>
              <th>Last Updated</th>
              <th>Completed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.execution_id}>
                <td>
                  {job.document_name ? (
                    <>
                      <strong>{job.document_name}</strong>
                      <br />
                      <small className="text-muted">{getTemplateName(job.template_id)}</small>
                    </>
                  ) : (
                    <strong>{getTemplateName(job.template_id)}</strong>
                  )}
                  <br />
                  <small className="text-muted font-monospace">
                    {job.execution_id.substring(0, 8)}...
                  </small>
                </td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(job.status)}`}>
                    <i className={`bi ${getStatusIcon(job.status)} me-1`}></i>
                    {job.status}
                  </span>
                  {job.status === 'failed' && job.error && (
                    <div className="mt-1">
                      <small className="text-danger" title={job.error}>
                        <i className="bi bi-exclamation-triangle"></i> Error
                      </small>
                    </div>
                  )}
                </td>
                <td>
                  <small>{formatDate(job.started_at)}</small>
                </td>
                <td>
                  <small>{job.updated_at ? formatDate(job.updated_at) : formatDate(job.started_at)}</small>
                </td>
                <td>
                  <small>{job.completed_at ? formatDate(job.completed_at) : '-'}</small>
                </td>
                 <td>
                  <div className="btn-group btn-group-sm" role="group">
                    {job.status === 'completed' && job.output && (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => handlePreview(job)}
                          disabled={previewLoading}
                          title="Preview PDF"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => handleDownload(job, 'pdf')}
                          title="Download PDF"
                        >
                          <i className="bi bi-file-pdf"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => handleDownload(job, 'docx')}
                          title="Download DOCX"
                        >
                          <i className="bi bi-file-word"></i>
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleEditNameClick(job)}
                      disabled={editingNameJobId === job.execution_id}
                      title="Edit Document Name"
                    >
                      {editingNameJobId === job.execution_id ? (
                        <i className="bi bi-hourglass-split"></i>
                      ) : (
                        <i className="bi bi-pencil"></i>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => handleDeleteClick(job)}
                      disabled={deletingJobId === job.execution_id}
                      title="Delete Job"
                    >
                      {deletingJobId === job.execution_id ? (
                        <i className="bi bi-hourglass-split"></i>
                      ) : (
                        <i className="bi bi-trash"></i>
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-pdf me-2"></i>
                  {previewFilename}
                </h5>
                <button type="button" className="btn-close" onClick={closePreview}></button>
              </div>
              <div className="modal-body p-0" style={{ height: '80vh' }}>
                <iframe
                  src={previewUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Document Preview"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closePreview}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && jobToDelete && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                  Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={handleDeleteCancel}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this document generation job?</p>
                <div className="bg-light p-3 rounded">
                  <strong>Job Details:</strong>
                  <br />
                  <small className="text-muted">Template:</small> {getTemplateName(jobToDelete.template_id)}
                  <br />
                  <small className="text-muted">ID:</small> <span className="font-monospace">{jobToDelete.execution_id.substring(0, 8)}...</span>
                  <br />
                  <small className="text-muted">Status:</small> <span className={`badge ${getStatusBadgeClass(jobToDelete.status)} me-1`}>{jobToDelete.status}</span>
                </div>
                <div className="alert alert-warning mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Warning:</strong> This action cannot be undone. The job record and all associated generated files will be permanently deleted.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleDeleteCancel}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm}>
                  <i className="bi bi-trash me-2"></i>
                  Delete Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Name Modal */}
      {showEditNameModal && jobToEdit && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil text-primary me-2"></i>
                  Edit Document Name
                </h5>
                <button type="button" className="btn-close" onClick={handleEditNameCancel}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="editDocumentName" className="form-label">Document Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="editDocumentName"
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    placeholder="Enter a friendly name for this document..."
                    autoFocus
                  />
                  <div className="form-text text-muted">
                    This name will be displayed in the jobs list
                  </div>
                </div>
                <div className="bg-light p-3 rounded">
                  <small className="text-muted">Template:</small> {getTemplateName(jobToEdit.template_id)}
                  <br />
                  <small className="text-muted">ID:</small> <span className="font-monospace">{jobToEdit.execution_id.substring(0, 8)}...</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleEditNameCancel}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleEditNameConfirm}>
                  <i className="bi bi-check me-2"></i>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default DocumentGenerationJobsTable;
